import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { ReactNode } from 'react'

import { cn } from './cn'

/**
 * Shared theme-aware prose styling for rendered Markdown. Centralizes the
 * (previously copy-pasted) prose-* class string so every content prop renders
 * markdown identically: muted body text, themed inline code, primary links.
 */
const PROSE_CLASSES =
  'prose max-w-none text-muted-foreground ' +
  'prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground ' +
  'prose-a:text-primary prose-a:no-underline hover:prose-a:underline ' +
  'prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 ' +
  'prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none ' +
  'prose-pre:bg-muted prose-pre:text-foreground'

const SIZE_CLASS = { sm: 'prose-sm', base: 'prose-base', lg: 'prose-lg' } as const

export interface MarkdownProps {
  /** Markdown source string. Renders nothing when empty/undefined. */
  children?: string
  /** Prose text scale. @default 'base' */
  size?: 'sm' | 'base' | 'lg'
  /**
   * Render inline: no surrounding prose block and paragraphs unwrap to their
   * inline content. Use inside an already-styled element (a quote, a single
   * display line) where a block `<p>` would break the layout.
   */
  inline?: boolean
  /** Extra classes merged onto the prose wrapper (e.g. margins, alignment). */
  className?: string
}

/**
 * Shared Markdown renderer for @mdxui/neo content props.
 *
 * Renders GitHub-flavored Markdown — **bold**, *italic*, `inline code`,
 * [links](url), lists, > blockquotes, and tables — with neo's theme-aware
 * prose styling. Every body/description content prop in neo flows through this
 * so authored markdown looks consistent across components.
 */
export function Markdown({ children, size = 'base', inline = false, className }: MarkdownProps): ReactNode {
  if (!children) return null

  if (inline) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ p: ({ children }) => <>{children}</> }}
      >
        {children}
      </ReactMarkdown>
    )
  }

  return (
    <div className={cn(PROSE_CLASSES, SIZE_CLASS[size], className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
    </div>
  )
}
